import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import Navbar from "../components/Navbar";
import {
  buildProductDetailsPath,
  clampDiscountRate,
  resolveProductImageUrl,
} from "../utils/commerce";
import "./Products.css";

const normalizeCategory = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getProductCategories = (product) => {
  if (Array.isArray(product.categories)) {
    return product.categories.map(normalizeCategory).filter(Boolean);
  }

  if (typeof product.category === "string") {
    return [normalizeCategory(product.category)].filter(Boolean);
  }

  if (typeof product.categories === "string") {
    return product.categories.split(",").map(normalizeCategory).filter(Boolean);
  }

  return [];
};

const formatDiscountPercent = (value) => {
  const rate = Number(value || 0);

  if (Number.isNaN(rate)) {
    return "0";
  }

  return Number(rate.toFixed(2)).toString();
};

const getProductCardData = (product) => {
  const discountRate = clampDiscountRate(product.discountRate);
  const basePrice = getProductDisplayPrice(product);
  const discountedPrice = basePrice * (1 - discountRate / 100);

  return {
    discountRate,
    basePrice,
    discountedPrice,
    imageUrl: resolveProductImageUrl(product),
    categories: getProductCategories(product),
  };
};

const getProductDisplayPrice = (product) => {
  const basePrice = Number(product?.price);

  if (Number.isFinite(basePrice) && basePrice > 0) {
    return basePrice;
  }

  const variantPrices = Array.isArray(product?.variants)
    ? product.variants
        .map((variant) => Number(variant?.price))
        .filter((price) => Number.isFinite(price) && price >= 0)
    : [];

  if (variantPrices.length > 0) {
    return Math.min(...variantPrices);
  }

  return 0;
};

const getTotalAvailableStock = (product) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];

  if (variants.length > 0) {
    return variants.reduce(
      (sum, variant) => sum + Math.max(0, Number(variant?.stock || 0)),
      0,
    );
  }

  return Math.max(0, Number(product?.stock || 0));
};

const getDefaultVariantNameForCart = (product) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];

  if (variants.length === 0) {
    return "";
  }

  const firstInStock = variants.find(
    (variant) => Number(variant?.stock || 0) > 0,
  );
  return String((firstInStock || variants[0])?.name || "").trim();
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Products | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Browse products on Balitah and discover the latest local marketplace deals.",
    );
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await api.getProducts();
        setProducts(data.data || []);
      } catch (error) {
        console.error("Failed to load products:", error);
        alert("Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const addToCart = async (product, e) => {
    e.stopPropagation();
    setAddingToCart(product._id);

    const availableStock = getTotalAvailableStock(product);
    const defaultVariantName = getDefaultVariantNameForCart(product);

    if (availableStock <= 0) {
      alert("This item is out of stock");
      setAddingToCart(null);
      return;
    }

    try {
      const response = await api.addToCart(product._id, 1, defaultVariantName);

      if (!response?.success) {
        const message = String(response?.message || "").toLowerCase();
        if (
          message.includes("not logged in") ||
          message.includes("not authorized")
        ) {
          navigate("/login");
          return;
        }
        alert(response?.message || "Failed to add to cart");
        return;
      }

      alert("Added to cart successfully");
    } catch (error) {
      console.error("Failed to add to cart:", error);
      alert("Failed to add to cart");
    } finally {
      setAddingToCart(null);
    }
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const normalizedCategoryFilter = normalizeCategory(categoryFilter);
  const filteredProducts = products.filter((product) => {
    const name = (product.name || "").toLowerCase();
    const description = (product.description || "").toLowerCase();
    const categories = getProductCategories(product);
    const categoriesText = categories.join(" ");

    const matchesSearch = normalizedQuery
      ? name.includes(normalizedQuery) ||
        description.includes(normalizedQuery) ||
        categoriesText.includes(normalizedQuery)
      : true;

    const matchesCategory = normalizedCategoryFilter
      ? categories.some(
          (category) =>
            category === normalizedCategoryFilter ||
            category.includes(normalizedCategoryFilter),
        )
      : true;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="products-page">
      <Navbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        categoryFilter={categoryFilter}
        onCategoryChange={setCategoryFilter}
        showCategoryFilter
        showSearch
      />

      <div className="products-container">
        {/* Header */}
        <div className="products-header">
          <h1>Browse Products</h1>
          <p>Discover our amazing collection of products</p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="products-grid">
            <div className="products-loading">
              <div className="products-spinner"></div>
              <p>Loading products...</p>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          /* Empty State */
          <div className="products-grid">
            <div className="products-empty">
              <div className="products-empty-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M16 11V7a4 4 0 0 0-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h2>No products found</h2>
              <p>
                {searchQuery || categoryFilter
                  ? "Try adjusting your search or filters"
                  : "Check back soon for new products"}
              </p>
            </div>
          </div>
        ) : (
          /* Products Grid */
          <div className="products-grid">
            {filteredProducts.map((p) => {
              const {
                discountRate,
                basePrice,
                discountedPrice,
                imageUrl,
                categories,
              } = getProductCardData(p);

              return (
                <div
                  key={p._id}
                  className="product-card"
                  onClick={() => navigate(buildProductDetailsPath(p))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      navigate(buildProductDetailsPath(p));
                    }
                  }}
                >
                  {/* Image Container */}
                  <div className="product-image-container">
                    {imageUrl ? (
                      <img src={imageUrl} alt={p.name} />
                    ) : (
                      <div className="product-no-image">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </div>
                    )}

                    {/* Discount Badge */}
                    {discountRate > 0 && (
                      <span className="product-discount-badge">
                        -{formatDiscountPercent(discountRate)}%
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="product-content">
                    {/* Categories */}
                    {categories.length > 0 && (
                      <div className="product-categories">
                        {categories.slice(0, 2).map((cat, i) => (
                          <span key={i} className="product-category-tag">
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Product Name */}
                    <h3 className="product-name">{p.name}</h3>

                    {/* Pricing */}
                    <div className="product-pricing">
                      {discountRate > 0 ? (
                        <>
                          <span className="product-price-current">
                            BND {discountedPrice.toFixed(2)}
                          </span>
                          <span className="product-price-original">
                            BND {basePrice.toFixed(2)}
                          </span>
                          <span className="product-price-save">
                            Save {formatDiscountPercent(discountRate)}%
                          </span>
                        </>
                      ) : (
                        <span className="product-price-current">
                          BND {basePrice.toFixed(2)}
                        </span>
                      )}
                    </div>

                    {/* Add to Cart Button */}
                    <button
                      className="product-add-btn"
                      onClick={(e) => addToCart(p, e)}
                      disabled={addingToCart === p._id}
                    >
                      {addingToCart === p._id ? (
                        <>
                          <div className="product-btn-spinner"></div>
                          Adding...
                        </>
                      ) : (
                        <>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="9" cy="21" r="1" />
                            <circle cx="20" cy="21" r="1" />
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                          </svg>
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating Cart Button */}
      <button
        className="floating-cart-btn"
        onClick={() => navigate("/cart")}
        title="Go to Cart"
      >
        🛒
      </button>
    </div>
  );
}
